import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Typography,
  message,
  Popconfirm,
  Form,
  Modal,
  Select,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  TeamOutlined,
  UserOutlined,
  MailOutlined,
} from '@ant-design/icons';
import api from '../../services/api';
import { ProjectTeamAssignment, Team } from '../../types';
import { useLookups } from '../../context/LookupsContext';

const { Text } = Typography;

interface TeamAssignmentsProps {
  projectId: string;
  teamAssignments?: ProjectTeamAssignment[];
  onUpdate?: () => void;
}

const TeamAssignments: React.FC<TeamAssignmentsProps> = ({
  projectId,
  teamAssignments: initialAssignments,
  onUpdate,
}) => {
  const { teams, getTeamName, isLoading: lookupsLoading } = useLookups();

  // Debug logging
  useEffect(() => {
    console.log('TeamAssignments - teams from context:', teams);
    console.log('TeamAssignments - lookupsLoading:', lookupsLoading);
  }, [teams, lookupsLoading]);
  const [assignments, setAssignments] = useState<ProjectTeamAssignment[]>(initialAssignments || []);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<ProjectTeamAssignment | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (initialAssignments) {
      setAssignments(initialAssignments);
    }
  }, [initialAssignments]);

  const loadAssignments = async () => {
    setLoading(true);
    try {
      const data = await api.getProjectTeamAssignments(projectId);
      setAssignments(data);
    } catch (error) {
      console.error('Failed to load team assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingAssignment(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (assignment: ProjectTeamAssignment) => {
    setEditingAssignment(assignment);
    form.setFieldsValue({
      teamCode: assignment.teamCode,
      email: assignment.email,
      name: assignment.name,
    });
    setModalVisible(true);
  };

  const handleDelete = async (assignmentId: string) => {
    try {
      await api.removeProjectTeamAssignment(projectId, assignmentId);
      message.success('Team assignment removed');
      loadAssignments();
      onUpdate?.();
    } catch (error) {
      message.error('Failed to remove team assignment');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await api.upsertProjectTeamAssignment(projectId, {
        teamCode: values.teamCode,
        email: values.email,
        name: values.name,
      });
      message.success(editingAssignment ? 'Team assignment updated' : 'Team assignment added');
      setModalVisible(false);
      form.resetFields();
      loadAssignments();
      onUpdate?.();
    } catch (error) {
      message.error('Failed to save team assignment');
    }
  };

  // All teams are available - multiple people can be assigned to the same team
  const availableTeams = teams;

  const columns = [
    {
      title: 'Team',
      dataIndex: 'teamCode',
      key: 'team',
      width: 200,
      render: (teamCode: string, record: ProjectTeamAssignment) => (
        <Space>
          <TeamOutlined />
          <div>
            <Text strong>{getTeamName(teamCode)}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>{teamCode}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Assigned Person',
      key: 'person',
      render: (_: any, record: ProjectTeamAssignment) => (
        <Space direction="vertical" size={0}>
          {record.name && (
            <Space>
              <UserOutlined />
              <Text>{record.name}</Text>
            </Space>
          )}
          <Space>
            <MailOutlined />
            <Text copyable={{ text: record.email }}>{record.email}</Text>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: any, record: ProjectTeamAssignment) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Remove this team assignment?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title={
          <Space>
            <TeamOutlined />
            <span>Team Assignments</span>
          </Space>
        }
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Team
          </Button>
        }
        style={{ marginTop: 24 }}
      >
        <Table
          columns={columns}
          dataSource={assignments}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
          locale={{ emptyText: 'No team assignments yet. Add teams to assign responsible people.' }}
        />
      </Card>

      <Modal
        title={editingAssignment ? 'Edit Team Assignment' : 'Add Team Assignment'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText={editingAssignment ? 'Update' : 'Add'}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="teamCode"
            label="Team"
            rules={[{ required: true, message: 'Please select a team' }]}
          >
            <Select
              placeholder={lookupsLoading ? "Loading teams..." : "Select team"}
              disabled={!!editingAssignment}
              showSearch
              optionFilterProp="label"
              loading={lookupsLoading}
              options={availableTeams.map((team) => ({
                value: team.code,
                label: `${team.name} (${team.code})`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="john@example.com" />
          </Form.Item>
          <Form.Item name="name" label="Name (optional)">
            <Input prefix={<UserOutlined />} placeholder="John Doe" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default TeamAssignments;
